import { toast } from "@/components/ui";
import { BodyText } from "@/components/ui/Text";
import { useCreateAllergy } from "@/features/Patient/MedicalRecords/MedicationsAllergies/network/useAllergies";
import { useState } from "react";
import { CiqAllergyMetadata } from "./CiqAllergyMetadata";
import { CiqAllergySingleRow } from "./CiqAllergySingleRow";
import type {
  AmazonRxcuiApothraMismatch,
  CodifiedAllergen,
} from "./useAmazonVisitIntake";
import { useMarkAllergenAddedToChart } from "./useMarkAllergenAddedToChart";

const DOSESPOT_SYSTEM = "dosespotv2";

interface CiqAllergyMismatchProps {
  mismatch: AmazonRxcuiApothraMismatch;
  patientId: string;
  encounterId?: string;
  questionnaireResponseId?: string;
}

export const CiqAllergyMismatch = ({
  mismatch,
  patientId,
  encounterId,
  questionnaireResponseId,
}: CiqAllergyMismatchProps) => {
  const [wasAutoCharted] = useState(Boolean(mismatch.addedToChartAt));
  const [isAdded, setIsAdded] = useState(Boolean(mismatch.addedToChartAt));

  const { mutateAsync: createAllergy, isPending } = useCreateAllergy({
    patientId,
  });
  const { mutateAsync: markAllergenAddedToChart } =
    useMarkAllergenAddedToChart();

  const allergenName = mismatch.allergenName ?? "Unknown allergen";
  const allergen: CodifiedAllergen = {
    name: allergenName,
    allergenId: mismatch.allergenId,
    system: DOSESPOT_SYSTEM,
    addedToChartAt: mismatch.addedToChartAt,
  };

  const handleAdd = async () => {
    if (!mismatch.allergenId) {
      toast.error(`Failed to add ${allergenName} to allergies`);
      return;
    }
    try {
      await createAllergy({
        allergyCode: mismatch.allergenId,
        allergyCodeSystem: DOSESPOT_SYSTEM,
        allergyName: allergenName,
        allergyType: "allergy",
        encounterId,
      });
      setIsAdded(true);
    } catch {
      return;
    }

    if (questionnaireResponseId) {
      try {
        await markAllergenAddedToChart({
          questionnaireResponseId,
          allergenId: mismatch.allergenId,
        });
      } catch {}
    }
  };

  return (
    <div className="border-l-4 border-ai pl-2 py-0.5 rounded">
      <BodyText size="sm" bold>
        {allergenName}
      </BodyText>
      <CiqAllergyMetadata type="allergy" severity={null} symptoms={[]} />
      <CiqAllergySingleRow
        allergen={allergen}
        onAdd={handleAdd}
        isPending={isPending}
        isAdded={isAdded}
        addedLabel={wasAutoCharted ? "Auto-charted" : "Added"}
      />
    </div>
  );
};
